<template>
  <div :class="['nueva-grilla-costos', { 'modo-compacto': modoCompacto }]">
    <div class="grilla-header">
      <div class="grilla-header__info">
        <span
          v-if="cotizacion.Codigo"
          class="grilla-header__title"
        >
          Cotización N° {{ cotizacion.Codigo }}
        </span>
        <span class="grilla-header__subtitle">
          {{ cantidadAlternativas }}
          {{ cantidadAlternativas === 1 ? 'alternativa de cantidad' : 'alternativas de cantidad' }}
        </span>
      </div>

      <div class="grilla-header__actions">
        <el-popover
          placement="bottom-end"
          :width="360"
          trigger="click"
          @show="onPopoverShow"
          @hide="onPopoverHide"
        >
          <template #reference>
            <el-button size="default">
              <template #icon>
                <IconTune />
              </template>
              Personalizar vista
            </el-button>
          </template>

          <div class="vista-config">
            <div class="vista-config__section">
              <div class="vista-config__title">Formato de descripción</div>
              <el-radio-group
                v-model="formatoDescripcion"
                class="vista-config__radios"
              >
                <el-radio value="default">Por defecto</el-radio>
                <el-radio value="proceso_material">Proceso + Material</el-radio>
                <el-radio value="proceso_categoria">Proceso + Categoría</el-radio>
              </el-radio-group>
            </div>

            <div
              v-if="cantidadAlternativas >= 3"
              class="vista-config__section vista-config__row"
            >
              <div>
                <div class="vista-config__title">Modo compacto</div>
                <div class="vista-config__hint">Muestra solo cantidad y costo</div>
              </div>
              <el-switch v-model="modoCompacto" />
            </div>

            <div class="vista-config__section">
              <div class="vista-config__row">
                <div class="vista-config__title">Columnas visibles</div>
                <el-switch
                  v-if="cantidadAlternativas >= 2"
                  v-model="esModoGlobal"
                  active-text="Global"
                  inactive-text="Por alternativa"
                  inline-prompt
                  style="--el-switch-on-color: var(--el-color-primary)"
                />
              </div>
              <div class="vista-config__hint">
                {{
                  esModoGlobal
                    ? `Se aplica a las ${cantidadAlternativas} cantidades. Arrastrá para cambiar el orden`
                    : 'Configurá cada cantidad por separado. Arrastrá para cambiar el orden'
                }}
              </div>
              <div
                v-if="esModoGlobal"
                ref="globalListRef"
                class="column-config-list"
              >
                <div
                  v-for="item in globalConfig"
                  :key="item.field"
                  class="column-config-item"
                >
                  <IconDrag class="drag-handle" />
                  <el-checkbox v-model="item.visible">
                    {{ etiquetaCampo(item.field) }}
                  </el-checkbox>
                </div>
              </div>
              <div
                v-else
                ref="columnListRef"
                class="column-config-list"
              >
                <div
                  v-for="col in columnsConfig"
                  :key="col.id"
                  class="column-config-item"
                >
                  <IconDrag class="drag-handle" />
                  <el-checkbox v-model="col.visible">
                    {{ col.baseLabel }} · Alt. {{ col.suffix }}
                  </el-checkbox>
                </div>
              </div>
            </div>

            <div class="vista-config__footer">
              <el-button
                link
                type="primary"
                size="small"
                @click="restablecerPorDefecto"
              >
                <template #icon>
                  <IconRestore />
                </template>
                Restablecer por defecto
              </el-button>
            </div>
          </div>
        </el-popover>

        <el-tooltip
          content="Copiar grilla al portapapeles"
          placement="top"
        >
          <el-button
            size="default"
            aria-label="Copiar grilla al portapapeles"
            @click="copiarGrilla"
          >
            <template #icon>
              <IconContentCopy />
            </template>
          </el-button>
        </el-tooltip>

        <el-tooltip
          content="Imprimir PDF"
          placement="top"
        >
          <el-button
            size="default"
            aria-label="Imprimir PDF"
            :disabled="!cotizacion.Codigo"
            @click="imprimirPdf"
          >
            <template #icon>
              <IconPrinter />
            </template>
          </el-button>
        </el-tooltip>

        <el-button
          v-if="canEditCostsGrid"
          size="default"
          type="primary"
          @click="abrirModalNuevoConcepto"
        >
          <template #icon>
            <IconPlus />
          </template>
          Agregar costo / concepto
        </el-button>
      </div>
    </div>

    <div
      v-if="gruposDisponibles.length >= 2"
      class="grilla-cantidades"
    >
      <span class="grilla-cantidades__label">Cantidad activa</span>
      <div class="navegador-cantidad">
        <el-button
          size="small"
          :disabled="!puedeIrAnterior"
          @click="irAnterior"
        >
          <template #icon>
            <IconChevronLeft />
          </template>
        </el-button>
        <el-radio-group
          :model-value="cantidadActiva"
          size="small"
          @update:model-value="seleccionarCantidad"
        >
          <el-radio-button :value="null">Todas</el-radio-button>
          <el-radio-button
            v-for="grupo in gruposDisponibles"
            :key="grupo.altIndex"
            :value="grupo.altIndex"
          >
            {{ grupo.label }}
          </el-radio-button>
        </el-radio-group>
        <el-button
          size="small"
          :disabled="!puedeIrSiguiente"
          @click="irSiguiente"
        >
          <template #icon>
            <IconChevronRight />
          </template>
        </el-button>
      </div>
    </div>

    <div
      v-for="bloque in bloquesOrdenados"
      :key="bloque.clave"
      class="grilla-bloque"
    >
      <h3
        v-if="bloque.titulo"
        class="grilla-bloque__titulo"
      >
        {{ bloque.titulo }}
      </h3>
      <el-table
        :key="`${bloque.clave}-${claveTabla}`"
        border
        :default-sort="defaultSort"
        :fit="!modoCompacto"
        :data="bloque.datos"
        row-key="__rowKey"
        :tree-props="{ children: 'children' }"
        :default-expand-all="false"
        :row-class-name="obtenerClaseFila"
        @sort-change="onSortChange"
      >
        <el-table-column
          fixed
          prop="Descripcion"
          label="Costo"
          :width="modoCompacto ? 200 : 260"
          sortable="custom"
        >
          <template #default="{ row }">
            <span
              class="celda-descripcion"
              :class="{ 'descripcion-personalizada': esConceptoPersonalizado(row) }"
            >
              <el-tooltip
                v-if="getCostTooltip(row)"
                :content="getCostTooltip(row)"
                placement="top"
              >
                <span>{{ obtenerDescripcionFormateada(row) }}</span>
              </el-tooltip>
              <span v-else>{{ obtenerDescripcionFormateada(row) }}</span>
              <el-tooltip
                v-if="canEditCostsGrid && esFilaMovible(row)"
                :content="esFilaAdicional(row) ? 'Pasar a costo normal' : 'Marcar como adicional'"
                placement="top"
              >
                <el-button
                  class="btn-cambiar-adicional"
                  :class="{ 'is-loading': moviendoRowKey === row.__rowKey }"
                  link
                  type="primary"
                  size="default"
                  :loading="moviendoRowKey === row.__rowKey"
                  :aria-label="
                    esFilaAdicional(row) ? 'Pasar a costo normal' : 'Marcar como adicional'
                  "
                  @click.stop="cambiarAdicional(row)"
                >
                  <template #icon>
                    <IconArrowDown v-if="!esFilaAdicional(row)" />
                    <IconArrowUp v-else />
                  </template>
                </el-button>
              </el-tooltip>
              <el-button
                v-if="canEditCostsGrid && esConceptoPersonalizado(row)"
                class="btn-eliminar-concepto"
                link
                type="danger"
                size="default"
                :loading="eliminandoRowKey === row.__rowKey"
                title="Eliminar concepto"
                @click.stop="confirmarEliminarConcepto(row)"
              >
                <template #icon>
                  <IconDelete />
                </template>
              </el-button>
            </span>
          </template>
        </el-table-column>

        <el-table-column
          fixed
          prop="MedidaAbreviada"
          label="Medida"
          align="right"
          :width="modoCompacto ? 60 : 90"
          sortable="custom"
        />

        <el-table-column
          v-for="grupo in gruposColumnas"
          :key="grupo.altIndex"
          :label="grupo.label"
          align="center"
          class-name="grupo-alternativa-column"
        >
          <el-table-column
            v-for="col in grupo.columns"
            :key="col.id"
            :prop="`${col.field}${col.suffix}`"
            :label="col.baseLabel"
            :width="modoCompacto ? ANCHO_COMPACTO[col.field] || col.width : col.width"
            sortable="custom"
            :resizable="col.resizable"
            align="right"
            :class-name="
              col.id === grupo.columns[grupo.columns.length - 1].id ? 'fin-grupo-alternativa' : ''
            "
          >
            <template #default="scope">
              <template v-if="esCeldaEditable(scope.row, col)">
                <span
                  class="celda-editable"
                  @dblclick="iniciarEdicion(scope.row, col)"
                >
                  <span
                    v-if="scope.row[`EsEditadoManualmente${col.suffix}`]"
                    class="badge-editado"
                    title="Editado manualmente"
                  />
                  <el-input-number
                    v-if="enEdicion(scope.row, col)"
                    v-model="editingValue"
                    size="small"
                    :controls="false"
                    :precision="decimalesImportes"
                    @blur="confirmarEdicion(scope.row, col)"
                    @keyup.enter="confirmarEdicion(scope.row, col)"
                    @keyup.esc="cancelarEdicion"
                  />
                  <el-icon
                    v-else-if="celdaGuardando(scope.row, col)"
                    class="is-loading"
                  >
                    <Loading />
                  </el-icon>
                  <span v-else>{{ formatColumnValue(col, scope.row) }}</span>
                  <span
                    v-if="canEditCostsGrid"
                    class="cell-actions"
                  >
                    <IconEdit
                      v-if="!enEdicion(scope.row, col)"
                      class="icon-edit"
                      @click.stop="iniciarEdicion(scope.row, col)"
                    />
                    <el-button
                      v-if="scope.row[`EsEditadoManualmente${col.suffix}`] === true"
                      type="danger"
                      :icon="RefreshLeft"
                      circle
                      size="small"
                      @click.stop="restaurarCelda(scope.row, col)"
                    />
                  </span>
                </span>
              </template>
              <template v-else-if="col.field === 'Cantidad'">
                <span v-if="scope.row.Utilidad !== undefined">
                  <el-input
                    :model-value="cotizacion.CotizacionCantidad[col.altIndex].Utilidad"
                    :input-style="{ textAlign: 'right', width: '90%' }"
                    :disabled="!canEditCostsGrid"
                    @update:model-value="setCantidadField(col.altIndex, 'Utilidad', $event)"
                  />
                  <span> %</span>
                </span>
                <span v-else-if="scope.row.Comision !== undefined">
                  <el-input
                    :model-value="cotizacion.CotizacionCantidad[col.altIndex].Comision"
                    :input-style="{ textAlign: 'right', width: '90%' }"
                    :disabled="!canEditCostsGrid"
                    @update:model-value="setCantidadField(col.altIndex, 'Comision', $event)"
                  />
                  <span> %</span>
                </span>
                <span v-else-if="scope.row.Flete !== undefined">
                  <el-input
                    :model-value="cotizacion.CotizacionCantidad[col.altIndex].PorcentajeFlete"
                    :input-style="{ textAlign: 'right', width: '90%' }"
                    :disabled="!canEditCostsGrid"
                    @update:model-value="setCantidadField(col.altIndex, 'PorcentajeFlete', $event)"
                  />
                  <span> %</span>
                </span>
                <span v-else-if="scope.row.MermaAdicional !== undefined">
                  <el-input
                    :model-value="cotizacion.CotizacionCantidad[col.altIndex].MermaAdicional"
                    :input-style="{ textAlign: 'right', width: '90%' }"
                    :disabled="!canEditCostsGrid"
                    @update:model-value="setCantidadField(col.altIndex, 'MermaAdicional', $event)"
                  />
                  <span> %</span>
                </span>
                <span v-else-if="scope.row.AlicuotaIVA !== undefined">
                  <el-input
                    :model-value="cotizacion.CotizacionCantidad[col.altIndex].AlicuotaIVA"
                    :input-style="{ textAlign: 'right', width: '90%' }"
                    :disabled="true"
                  />
                  <span> %</span>
                </span>
                <span v-else>
                  {{ scope.row[`Cantidad${col.suffix}`] }}
                  {{
                    !scope.row.EsGrupoSubtotal &&
                    (scope.row[`EsConceptoPorcentual${col.suffix}`] === true ||
                      (scope.row.Descripcion &&
                        scope.row.Descripcion.toString().includes('calculada')))
                      ? '%'
                      : ''
                  }}
                </span>
              </template>
              <template v-else-if="col.field === 'Precio' && scope.row.EsFilaPrecioReferencia">
                <div class="precio-referencia-celda">
                  <span
                    v-if="canEditCostsGrid && !isACotizar"
                    :class="[
                      'precio-referencia-acciones',
                      {
                        'acciones--visibles':
                          puedeIgualarPrecio(col.altIndex) || igualandoAltIndex === col.altIndex,
                      },
                    ]"
                  >
                    <el-tooltip
                      v-if="puedeIgualarPrecio(col.altIndex)"
                      content="Igualar precio a este valor"
                      placement="top"
                    >
                      <el-button
                        size="small"
                        type="warning"
                        plain
                        circle
                        aria-label="Igualar precio"
                        :loading="igualandoAltIndex === col.altIndex"
                        @click="igualarPrecio(col.altIndex)"
                      >
                        <template #icon>
                          <IconEqual />
                        </template>
                      </el-button>
                    </el-tooltip>
                    <el-tooltip
                      v-if="cotizacion.CotizacionCantidad?.[col.altIndex]?.EsIgualada"
                      content="Deshacer igualación"
                      placement="top"
                    >
                      <el-button
                        size="small"
                        plain
                        circle
                        aria-label="Deshacer igualación"
                        :loading="deshaciendoAltIndex === col.altIndex"
                        @click="confirmarDeshacerIgualar(col.altIndex)"
                      >
                        <template #icon>
                          <IconUndo />
                        </template>
                      </el-button>
                    </el-tooltip>
                  </span>
                  <el-input-number
                    v-model="precioReferenciaEditado[col.altIndex]"
                    class="input-precio-referencia"
                    size="small"
                    :controls="false"
                    :min="0"
                    :precision="decimalesImportes"
                    :disabled="!canEditCostsGrid || isACotizar"
                    placeholder="Sin valor"
                  />
                </div>
              </template>
              <template v-else>
                {{ formatColumnValue(col, scope.row) }}
              </template>
            </template>
          </el-table-column>
        </el-table-column>
      </el-table>
    </div>

    <modal-nuevo-concepto-costo
      v-model:visible="modalNuevoConceptoVisible"
      v-model:form="formNuevoConcepto"
      :saving="savingConcepto"
      :alternativas="alternativasParaSelect"
      :costos="costos"
      @save="guardarNuevoConcepto"
    />
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  watch,
  onMounted,
  nextTick,
} from 'vue';
import { storeToRefs } from 'pinia';
import { ElMessageBox } from 'element-plus';
import Sortable from 'sortablejs';
import {
  Loading,
  RefreshLeft,
} from '@element-plus/icons-vue';
import { useConfiguracionStore } from '@/stores/configuracion-general.js';
import { useCostoStore } from '@/stores/costo/costo_store.js';
import { EstadoSolicitud } from '@/enum/estado_solicitud';
import { formatDecimal } from '@/shared/utils/format_decimal.js';
import cotizacionCostosService from '@/services/cotizacion_costos_service.js';
import { useMessageSuccess } from '@/shared/composables/message_success.js';
import { useMessageError } from '@/shared/composables/message_error.js';
import { useLoading } from '@/shared/composables/loading.js';
import ModalNuevoConceptoCosto from './modal_nuevo_concepto_costo.vue';
import { useAuthStore } from '@/views/auth/auth_store';
import { hasPermission } from '@/shared/permissions/permission.utils.js';
import IconTune from '~icons/mdi/tune';
import IconRestore from '~icons/mdi/restore';
import IconDrag from '~icons/mdi/drag';
import IconEdit from '~icons/mdi/edit';
import IconPlus from '~icons/mdi/plus';
import IconDelete from '~icons/mdi/delete';
import IconContentCopy from '~icons/mdi/content-copy';
import IconPrinter from '~icons/mdi/printer';
import { mensajeErrorApi } from '@/shared/utils/error_api.js';
import { resolveApiUrl } from '@/shared/utils/resolve_api_url.js';
import IconChevronLeft from '~icons/mdi/chevron-left';
import IconChevronRight from '~icons/mdi/chevron-right';
import IconEqual from '~icons/mdi/equal';
import IconUndo from '~icons/mdi/undo';
import IconArrowDown from '~icons/mdi/arrow-down-bold-box-outline';
import IconArrowUp from '~icons/mdi/arrow-up-bold-box-outline';

const props = defineProps({
  cotizacion: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['recargar-cotizacion']);

const configuracionStore = useConfiguracionStore();
const { decimalesImportes, mostrarValorUnidadVentaGrilla, mostrarValorUnidadProductoGrilla } =
  storeToRefs(configuracionStore);
const authStore = useAuthStore();
const costoStore = useCostoStore();
const { costos } = storeToRefs(costoStore);

const BASE_COLUMNS = [
  { field: 'Cantidad', label: 'Cantidad', width: 160 },
  { field: 'Precio', label: 'Costo', width: 130 },
  { field: 'PrecioUnitario', label: 'Costo Unitario', width: 140 },
  { field: 'ValorUnidadVenta', label: 'Valor por Unidad de Venta', width: 160 },
  { field: 'ValorUnidadProducto', label: 'Valor por Unidad de Producto', width: 170 },
];

const columnsConfig = ref([]);
const columnListRef = ref(null);
const globalListRef = ref(null);
const globalConfig = ref([]);
const modoVista = ref('global');
const MODOS_VISTA_VALIDOS = ['global', 'alternativa'];
const esModoGlobal = computed({
  get: () => modoVista.value === 'global',
  set: (value) => {
    modoVista.value = value ? 'global' : 'alternativa';
  },
});
let sortableInstance = null;

const formatoDescripcion = ref('default');
const modoCompacto = ref(false);
const COMPACT_FIELDS = ['Cantidad', 'Precio'];
const ANCHO_COMPACTO = { Cantidad: 90, Precio: 100 };

const SECCIONES_AGRUPABLES = ['Costos', 'Adicionales'];
const SECCION_CLASS_MAP = {
  Costos: 'section-costos',
  Adicionales: 'section-adicionales',
  Ajustes: 'section-ajustes',
  Totales: 'section-totales',
  Utilidad: 'section-utilidad',
};
const SECCIONES_VISIBLES_SIN_DETALLE_COSTOS = ['Totales', 'Impuestos'];

const setCantidadField = (altIndex, field, value) => {
  const alternativa = props.cotizacion.CotizacionCantidad?.[altIndex];
  if (!alternativa) return;
  alternativa[field] = value;
};

const isEdicionUtilidadComisionDeshabilitada = computed(() =>
  [EstadoSolicitud.CON_PROPUESTA, EstadoSolicitud.ACEPTADA, EstadoSolicitud.RECHAZADA].includes(
    props.cotizacion.CodigoEstado,
  ),
);

const isACotizar = computed(() => props.cotizacion.CodigoEstado === EstadoSolicitud.A_COTIZAR);

// Rol "Ejecutivos que cotizan": ve solo Precio unitario, Totales e Impuestos, sin edición.
const esEjecutivoCotiza = computed(() => authStore.esEjecutivoQueCotiza());

const canViewCostDetails = computed(
  () => hasPermission(authStore.usuario(), 'cotizacion_costos:read') && !esEjecutivoCotiza.value,
);

const canEditCostsGrid = computed(
  () =>
    hasPermission(authStore.usuario(), 'cotizacion_costos:edit') &&
    !esEjecutivoCotiza.value &&
    !isEdicionUtilidadComisionDeshabilitada.value,
);

const editingCell = ref({ rowKey: null, field: null, suffix: null });
const editingValue = ref(null);
const celdaEnProceso = ref({ rowKey: null, field: null, suffix: null });

const CAMPO_A_DTO = {
  Cantidad: 'Cantidad',
  Precio: 'PrecioTotal',
  PrecioUnitario: 'PrecioUnitario',
};

const esRowEditableInline = (row) =>
  !esFilaSintetica(row) && !row.EsGrupoSubtotal && row.ConceptoPorcentual !== true;

const esCeldaEditable = (row, col) =>
  Object.keys(CAMPO_A_DTO).includes(col.field) && esRowEditableInline(row);

const enEdicion = (row, col) =>
  editingCell.value.rowKey === row.__rowKey &&
  editingCell.value.field === col.field &&
  editingCell.value.suffix === col.suffix;

const celdaGuardando = (row, col) =>
  celdaEnProceso.value.rowKey === row.__rowKey &&
  celdaEnProceso.value.field === col.field &&
  celdaEnProceso.value.suffix === col.suffix;

const iniciarEdicion = (row, col) => {
  if (!canEditCostsGrid.value || !esCeldaEditable(row, col)) return;
  editingCell.value = { rowKey: row.__rowKey, field: col.field, suffix: col.suffix };
  editingValue.value = row[`${col.field}${col.suffix}`];
};

const cancelarEdicion = () => {
  editingCell.value = { rowKey: null, field: null, suffix: null };
  editingValue.value = null;
};

const confirmarEdicion = async (row, col) => {
  const valorOriginal = row[`${col.field}${col.suffix}`];
  const valorNuevo = editingValue.value;
  if (valorNuevo === valorOriginal || valorNuevo === '' || valorNuevo === null) {
    cancelarEdicion();
    return;
  }
  const idCosto = row[`CodigoCotizacionCantidadCosto${col.suffix}`];
  celdaEnProceso.value = { rowKey: row.__rowKey, field: col.field, suffix: col.suffix };
  try {
    await cotizacionCostosService.editarCostoManual(props.cotizacion.Codigo, idCosto, {
      [CAMPO_A_DTO[col.field]]: valorNuevo,
    });
    cancelarEdicion();
    emit('recargar-cotizacion');
  } catch (err) {
    useMessageError().open(err);
    cancelarEdicion();
  } finally {
    celdaEnProceso.value = { rowKey: null, field: null, suffix: null };
  }
};

const igualandoAltIndex = ref(null);

const precioReferenciaEditado = ref({});

const sincronizarPrecioReferencia = () => {
  const valores = {};
  (props.cotizacion.CotizacionCantidad || []).forEach((alt, i) => {
    valores[i] = alt.PrecioReferencia > 0 ? Number(alt.PrecioReferencia) : null;
  });
  precioReferenciaEditado.value = valores;
};

const puedeIgualarPrecio = (altIndex) => {
  const alt = props.cotizacion.CotizacionCantidad?.[altIndex];
  const valor = precioReferenciaEditado.value[altIndex];
  if (!alt || !(valor > 0)) return false;
  return valor !== Number(alt.PrecioReferencia) || valor !== Number(alt.PrecioTotal);
};

const igualarPrecio = async (altIndex) => {
  const alt = props.cotizacion.CotizacionCantidad?.[altIndex];
  if (!alt) return;
  igualandoAltIndex.value = altIndex;
  try {
    await cotizacionCostosService.igualarPrecio(
      props.cotizacion.Codigo,
      alt.Codigo,
      precioReferenciaEditado.value[altIndex],
    );
    useMessageSuccess().open('El precio se igualó correctamente.');
    emit('recargar-cotizacion');
  } catch (err) {
    useMessageError().open(
      'No es posible igualar a este precio de referencia, revisa los costos base.',
    );
  } finally {
    igualandoAltIndex.value = null;
  }
};

const deshaciendoAltIndex = ref(null);

const deshacerIgualar = async (altIndex) => {
  const alt = props.cotizacion.CotizacionCantidad?.[altIndex];
  if (!alt) return;
  deshaciendoAltIndex.value = altIndex;
  try {
    await cotizacionCostosService.deshacerIgualarPrecio(props.cotizacion.Codigo, alt.Codigo);
    useMessageSuccess().open('Se deshizo la igualación de precio.');
    emit('recargar-cotizacion');
  } catch (err) {
    useMessageError().open(err);
  } finally {
    deshaciendoAltIndex.value = null;
  }
};

const confirmarDeshacerIgualar = async (altIndex) => {
  try {
    await ElMessageBox.confirm(
      'Se restaurarán la utilidad y el precio de referencia previos a la igualación. ¿Continuar?',
      'Deshacer igualación',
      { confirmButtonText: 'Deshacer', cancelButtonText: 'Cancelar', type: 'warning' },
    );
  } catch {
    return;
  }
  await deshacerIgualar(altIndex);
};

const restaurarCelda = async (row, col) => {
  if (!canEditCostsGrid.value) return;
  const idCosto = row[`CodigoCotizacionCantidadCosto${col.suffix}`];
  celdaEnProceso.value = { rowKey: row.__rowKey, field: col.field, suffix: col.suffix };
  try {
    await cotizacionCostosService.restaurarCostoManual(props.cotizacion.Codigo, idCosto);
    useMessageSuccess().open('El valor se restauró correctamente.');
    cancelarEdicion();
    emit('recargar-cotizacion');
  } catch (err) {
    useMessageError().open(err);
  } finally {
    celdaEnProceso.value = { rowKey: null, field: null, suffix: null };
  }
};

const modalNuevoConceptoVisible = ref(false);
const savingConcepto = ref(false);
const ALTERNATIVA_TODAS = { Codigo: null, Descripcion: 'Todas las alternativas' };

const crearFormNuevoConcepto = () => ({
  Tipo: 'costo',
  Descripcion: '',
  Costo: null,
  Alternativa: ALTERNATIVA_TODAS,
  Cantidad: null,
  Precio: null,
  Porcentaje: null,
  ModoCalculo: 'suma_al_costo',
});

const formNuevoConcepto = ref(crearFormNuevoConcepto());

const alternativasParaSelect = computed(() => [
  ALTERNATIVA_TODAS,
  ...(props.cotizacion.CotizacionCantidad || []).map((alt, i) => ({
    Codigo: alt.Codigo,
    Descripcion:
      `Alternativa ${i + 1} · Cant. ${alt.Cantidad} ${props.cotizacion.UnidadVenta?.Abreviatura ?? ''}`.trim(),
  })),
]);

const abrirModalNuevoConcepto = () => {
  formNuevoConcepto.value = crearFormNuevoConcepto();
  modalNuevoConceptoVisible.value = true;
  if (!costos.value.length) {
    costoStore
      .findAllCostosHTTP({ page: 0, sortBy: 'Descripcion', sort: 'asc' })
      .catch((err) => useMessageError().open(err));
  }
};

const guardarNuevoConcepto = async () => {
  const form = formNuevoConcepto.value;
  const esConcepto = form.Tipo === 'concepto';
  const dto = esConcepto
    ? {
        TipoConcepto: 'PORCENTUAL',
        DescripcionPersonalizada: form.Descripcion,
        CodigoCotizacionCantidad: form.Alternativa?.Codigo ?? null,
        Porcentaje: form.Porcentaje,
        ModoCalculo: form.ModoCalculo,
      }
    : {
        TipoConcepto: 'COSTO',
        DescripcionPersonalizada: form.Descripcion,
        CodigoCosto: form.Costo?.Codigo ?? undefined,
        CodigoCotizacionCantidad: form.Alternativa?.Codigo ?? null,
        Cantidad: form.Cantidad,
        PrecioUnitario: form.Precio,
      };
  savingConcepto.value = true;
  try {
    useLoading().openLoading();
    await cotizacionCostosService.crearCostoPersonalizado(props.cotizacion.Codigo, dto);
    modalNuevoConceptoVisible.value = false;
    useMessageSuccess().open(
      esConcepto ? 'El concepto se agregó correctamente.' : 'El costo se agregó correctamente.',
    );
    emit('recargar-cotizacion');
  } catch (err) {
    useMessageError().open(err);
  } finally {
    savingConcepto.value = false;
    useLoading().closeLoading();
  }
};

const eliminandoRowKey = ref(null);

const obtenerIdsCostoPersonalizado = (row) =>
  (props.cotizacion.CotizacionCantidad || [])
    .map((_, i) => i + 1)
    .filter((suffix) => row[`EsConceptoPersonalizado${suffix}`] === true)
    .map((suffix) => row[`CodigoCotizacionCantidadCosto${suffix}`])
    .filter((id) => id !== null && id !== undefined);

const confirmarEliminarConcepto = async (row) => {
  try {
    await ElMessageBox.confirm(
      `¿Eliminar el concepto "${row.Descripcion}" de todas las alternativas?`,
      'Eliminar concepto',
      { confirmButtonText: 'Eliminar', cancelButtonText: 'Cancelar', type: 'warning' },
    );
  } catch {
    return;
  }

  const ids = obtenerIdsCostoPersonalizado(row);
  if (!ids.length) return;

  eliminandoRowKey.value = row.__rowKey;
  useLoading().openLoading();
  try {
    await Promise.all(
      ids.map((id) =>
        cotizacionCostosService.eliminarCostoPersonalizado(props.cotizacion.Codigo, id),
      ),
    );
    useMessageSuccess().open('El concepto se eliminó correctamente.');
  } catch (err) {
    useMessageError().open(err);
  } finally {
    eliminandoRowKey.value = null;
    useLoading().closeLoading();
    emit('recargar-cotizacion');
  }
};

const moviendoRowKey = ref(null);

const esFilaAdicional = (row) => row.SeccionGrilla === 'Adicionales';

const esFilaMovible = (row) =>
  ['Costos', 'Adicionales'].includes(row.SeccionGrilla) &&
  row.CostoTotal !== true &&
  !esFilaPrecioSintetica(row) &&
  !esConceptoPorcentual(row) &&
  obtenerIdsCosto(row).length > 0;

const esFilaPrecioSintetica = (row) => esFilaSintetica(row) && row.EsGrupoSubtotal !== true;

const esConceptoPorcentual = (row) =>
  Object.keys(row).some((k) => k.startsWith('EsConceptoPorcentual') && row[k] === true);

const obtenerIdsCosto = (row) => {
  const filas = row.children?.length ? row.children : [row];
  const sufijos = (props.cotizacion.CotizacionCantidad || []).map((_, i) => i + 1);
  return filas
    .flatMap((fila) => sufijos.map((s) => fila[`CodigoCotizacionCantidadCosto${s}`]))
    .filter((id) => id !== null && id !== undefined);
};

// TODO(COTI-639): cuando la grilla sea la fuente única, ocultar el switch "Adicional" de los procesos.
const cambiarAdicional = async (row) => {
  const pasarAAdicional = !esFilaAdicional(row);
  try {
    await ElMessageBox.confirm(
      pasarAAdicional
        ? 'El costo pasará a Adicionales en todas las alternativas y se recalculará el presupuesto. Los cambios sin cotizar en procesos se perderán. ¿Continuar?'
        : 'El adicional pasará a costo normal en todas las alternativas y se recalculará el presupuesto. Los cambios sin cotizar en procesos se perderán. ¿Continuar?',
      pasarAAdicional ? 'Marcar como adicional' : 'Pasar a costo normal',
      { confirmButtonText: 'Continuar', cancelButtonText: 'Cancelar', type: 'warning' },
    );
  } catch {
    return;
  }

  moviendoRowKey.value = row.__rowKey;
  useLoading().openLoading();
  try {
    await cotizacionCostosService.marcarAdicional(
      props.cotizacion.Codigo,
      obtenerIdsCosto(row),
      pasarAAdicional,
    );
    useMessageSuccess().open(
      pasarAAdicional ? 'El costo se marcó como adicional.' : 'El costo pasó a costo normal.',
    );
    emit('recargar-cotizacion');
  } catch (err) {
    useMessageError().open(err);
  } finally {
    moviendoRowKey.value = null;
    useLoading().closeLoading();
  }
};

const columnasEfectivas = computed(() => {
  if (!esModoGlobal.value) return columnsConfig.value;
  const alternativas = props.cotizacion.CotizacionCantidad || [];
  return alternativas.flatMap((_, altIndex) =>
    globalConfig.value.map(({ field, visible }) =>
      crearColumna(
        BASE_COLUMNS.find((b) => b.field === field),
        altIndex,
        visible,
      ),
    ),
  );
});

const columnasVisibles = computed(() => {
  const base = columnasEfectivas.value.filter((c) => c.visible);
  return modoCompacto.value ? base.filter((c) => COMPACT_FIELDS.includes(c.field)) : base;
});

const cantidadActiva = ref(null);

const cantidadAlternativas = computed(() => props.cotizacion.CotizacionCantidad?.length ?? 0);

const abreviaturaUnidadVenta = computed(() => props.cotizacion.UnidadVenta?.Abreviatura ?? '');

const gruposDisponibles = computed(() => {
  const grupos = [];
  const porAlt = new Map();
  columnasVisibles.value.forEach((col) => {
    let grupo = porAlt.get(col.altIndex);
    if (!grupo) {
      const alt = props.cotizacion.CotizacionCantidad?.[col.altIndex];
      grupo = {
        altIndex: col.altIndex,
        label:
          `Cantidad ${col.suffix}: ${alt ? alt.Cantidad : ''} ${abreviaturaUnidadVenta.value}`.trim(),
        columns: [],
      };
      porAlt.set(col.altIndex, grupo);
      grupos.push(grupo);
    }
    grupo.columns.push(col);
  });
  return grupos;
});

const gruposColumnas = computed(() =>
  cantidadActiva.value === null
    ? gruposDisponibles.value
    : gruposDisponibles.value.filter((g) => g.altIndex === cantidadActiva.value),
);

const claveTabla = computed(() => {
  const grupos = gruposColumnas.value
    .map((g) => `${g.altIndex}:${g.columns.map((c) => c.field).join(',')}`)
    .join('|');
  return `${modoCompacto.value ? 'c' : 'n'}|${grupos}`;
});

const indiceActivo = computed(() =>
  gruposDisponibles.value.findIndex((g) => g.altIndex === cantidadActiva.value),
);
const puedeIrAnterior = computed(() => indiceActivo.value > 0);
const puedeIrSiguiente = computed(() => indiceActivo.value < gruposDisponibles.value.length - 1);

const seleccionarCantidad = (altIndex) => {
  cantidadActiva.value = altIndex ?? null;
};

const irAnterior = () => {
  if (!puedeIrAnterior.value) return;
  cantidadActiva.value = gruposDisponibles.value[indiceActivo.value - 1].altIndex;
};

const irSiguiente = () => {
  if (!puedeIrSiguiente.value) return;
  cantidadActiva.value = gruposDisponibles.value[indiceActivo.value + 1].altIndex;
};

watch(gruposDisponibles, (grupos) => {
  if (cantidadActiva.value !== null && !grupos.some((g) => g.altIndex === cantidadActiva.value)) {
    cantidadActiva.value = null;
  }
});

const defaultVisibleByField = (field) => {
  if (field === 'ValorUnidadVenta') return !!mostrarValorUnidadVentaGrilla.value;
  if (field === 'ValorUnidadProducto') return !!mostrarValorUnidadProductoGrilla.value;
  return true;
};

const crearColumna = (base, altIndex, visible) => ({
  id: `${base.field}_${altIndex}`,
  field: base.field,
  altIndex,
  suffix: altIndex + 1,
  baseLabel: base.label,
  width: base.width,
  visible,
  resizable: true,
});

const buildDefaultColumnsConfig = () => {
  const alternativas = props.cotizacion.CotizacionCantidad || [];
  const cols = [];
  alternativas.forEach((alt, altIndex) => {
    BASE_COLUMNS.forEach((base) => {
      cols.push(crearColumna(base, altIndex, defaultVisibleByField(base.field)));
    });
  });
  return cols;
};

const buildDefaultGlobalConfig = () =>
  BASE_COLUMNS.map(({ field }) => ({ field, visible: defaultVisibleByField(field) }));

const etiquetaCampo = (field) => BASE_COLUMNS.find((b) => b.field === field)?.label ?? field;

const formatColumnValue = (col, row) => {
  const rawValue = row[`${col.field}${col.suffix}`];
  if (rawValue === null || rawValue === undefined || rawValue === '') return '';
  // Los conceptos porcentuales no tienen costo unitario (igual que el flete).
  if (col.field === 'PrecioUnitario' && row[`EsConceptoPorcentual${col.suffix}`] === true)
    return '';
  return formatDecimal(rawValue, decimalesImportes.value);
};

const getUsuarioId = () => {
  try {
    return JSON.parse(localStorage.getItem('usuario'))?.Codigo ?? '';
  } catch {
    return '';
  }
};

const storageKey = computed(() => `grillaCostosConfig_${getUsuarioId() || 'default'}`);

const formatoDescripcionStorageKey = computed(
  () => `grillaCostosFormatoDescripcion_${getUsuarioId() || 'default'}`,
);

const globalStorageKey = computed(() => `grillaCostosConfigGlobal_${getUsuarioId() || 'default'}`);

const modoVistaStorageKey = computed(() => `grillaCostosModoVista_${getUsuarioId() || 'default'}`);

const FORMATOS_DESCRIPCION_VALIDOS = ['default', 'proceso_material', 'proceso_categoria'];

const cargarFormatoDescripcion = () => {
  try {
    const saved = localStorage.getItem(formatoDescripcionStorageKey.value);
    if (FORMATOS_DESCRIPCION_VALIDOS.includes(saved)) {
      formatoDescripcion.value = saved;
    }
  } catch {}
};

watch(formatoDescripcion, (value) => {
  try {
    localStorage.setItem(formatoDescripcionStorageKey.value, value);
  } catch {}
});

const esFilaSintetica = (row) =>
  row.Utilidad !== undefined ||
  row.Comision !== undefined ||
  row.Flete !== undefined ||
  row.MermaAdicional !== undefined ||
  row.PrecioObjetivo !== undefined ||
  row.AlicuotaIVA !== undefined ||
  row.EsGrupoSubtotal === true ||
  row.EsFilaTotalAdicionales === true ||
  row.SeccionGrilla === 'Totales' ||
  row.SeccionGrilla === 'Utilidad';

const esFilaTotalConIVA = (row) =>
  row.SeccionGrilla === 'Totales' && /\+\s*iva/i.test(row.Descripcion ?? '');
const esFilaImpuesto = (row) => row.SeccionGrilla === 'Impuestos' || esFilaTotalConIVA(row);

// TODO: ocultas provisoriamente hasta implementar Merma adicional / Merma calculada en la nueva grilla.
const esFilaMerma = (row) =>
  row.MermaAdicional !== undefined || /merma/i.test(row.Descripcion ?? '');

const limpiar = (v) => (typeof v === 'string' ? v.replace(/\s+/g, ' ').trim() : '');

const esConceptoPersonalizado = (row) =>
  Object.keys(row).some((k) => k.startsWith('EsConceptoPersonalizado') && row[k] === true);

const obtenerDescripcionFormateada = (row) => {
  if (esFilaSintetica(row) || esConceptoPersonalizado(row)) return row.Descripcion;

  const descripcionBase = limpiar(row.DescripcionCosto) || row.Descripcion;
  if (formatoDescripcion.value === 'default') return descripcionBase;

  const proceso = limpiar(row.ProcesoNombre);
  const maquina = limpiar(row.MaquinaNombre);
  const categoria = limpiar(row.CategoriaMaterialNombre);
  const material = limpiar(row.MaterialNombre);

  let etiqueta = '';
  if (row.OrigenCosto === 'PROCESO') {
    etiqueta = [proceso, maquina].filter(Boolean).join(' - ');
  } else if (row.OrigenCosto === 'MATERIAL') {
    const detalle =
      formatoDescripcion.value === 'proceso_material' ? material || categoria : categoria;
    etiqueta = [proceso, detalle].filter(Boolean).join(' - ');
  }
  return etiqueta || descripcionBase;
};

const getCostTooltip = (row) => {
  if (esConceptoPersonalizado(row)) {
    return limpiar(row.TooltipConcepto) || `${row.Descripcion} - Concepto ingresado manualmente`;
  }
  if (esFilaSintetica(row)) return '';
  const descripcion = limpiar(row.DescripcionCosto);

  if (row.OrigenCosto === 'PROCESO') {
    const proceso = limpiar(row.ProcesoNombre);
    const maquina = limpiar(row.MaquinaNombre);
    return [descripcion, proceso && `Proceso: ${proceso}`, maquina && `Máquina: ${maquina}`]
      .filter(Boolean)
      .join(' | ');
  }
  if (row.OrigenCosto === 'MATERIAL') {
    const categoria = limpiar(row.CategoriaMaterialNombre);
    const material = limpiar(row.MaterialNombre);
    return [
      descripcion,
      categoria && `Categoría: ${categoria}`,
      material && `Material: ${material}`,
    ]
      .filter(Boolean)
      .join(' | ');
  }
  return '';
};

const slugify = (str) => String(str).trim().replace(/\s+/g, '_');

const sumarCampoHijos = (children, campo) =>
  children.reduce((sum, child) => {
    const valor = Number(child[campo]);
    return sum + (Number.isNaN(valor) ? 0 : valor);
  }, 0);

const construirNodoGrupo = (seccionGrilla, agrupacionCosto, children, suffixes) => {
  const parent = {
    Descripcion: `${agrupacionCosto} (Grupo)`,
    SeccionGrilla: seccionGrilla,
    EsGrupoSubtotal: true,
    MedidaAbreviada: '',
    children,
    __rowKey: `grupo-${slugify(seccionGrilla)}-${slugify(agrupacionCosto)}`,
  };

  suffixes.forEach((suffix) => {
    ['Precio', 'ValorUnidadVenta', 'ValorUnidadProducto'].forEach((field) => {
      parent[`${field}${suffix}`] = sumarCampoHijos(children, `${field}${suffix}`);
    });
    parent[`PrecioUnitario${suffix}`] = '';
    parent[`Cantidad${suffix}`] = '';
  });

  return parent;
};

const construirFilaTotalAdicionales = (filas, suffixes) => {
  const fila = {
    Descripcion: 'Total adicionales',
    SeccionGrilla: 'Adicionales',
    CostoTotal: true,
    EsFilaTotalAdicionales: true,
    MedidaAbreviada: '',
    __rowKey: 'sintetica-total-adicionales',
  };

  suffixes.forEach((suffix) => {
    ['Precio', 'ValorUnidadVenta', 'ValorUnidadProducto'].forEach((field) => {
      fila[`${field}${suffix}`] = sumarCampoHijos(filas, `${field}${suffix}`);
    });
    fila[`PrecioUnitario${suffix}`] = '';
    fila[`Cantidad${suffix}`] = '';
  });

  return fila;
};

const construirNodoImpuestos = (children, suffixes) => {
  const totalConIVA = children.find(esFilaTotalConIVA);
  const parent = {
    Descripcion: 'Impuestos',
    SeccionGrilla: 'Impuestos',
    EsGrupoSubtotal: true,
    MedidaAbreviada: '',
    children,
    __rowKey: 'grupo-impuestos',
  };

  suffixes.forEach((suffix) => {
    ['Precio', 'ValorUnidadVenta', 'ValorUnidadProducto'].forEach((field) => {
      parent[`${field}${suffix}`] = totalConIVA?.[`${field}${suffix}`] ?? '';
    });
    parent[`PrecioUnitario${suffix}`] = '';
    parent[`Cantidad${suffix}`] = '';
  });

  return parent;
};

const conUnidadVenta = (base) =>
  abreviaturaUnidadVenta.value ? `${base} por ${abreviaturaUnidadVenta.value}` : base;

const datosGrillaAgrupados = computed(() => {
  let filas = (props.cotizacion.CotizacionCantidadCostos || [])
    .filter((row) => !esFilaMerma(row) && row.PrecioObjetivo === undefined)
    .map((row) =>
      row.PrecioUnitario === true ? { ...row, Descripcion: conUnidadVenta('Precio') } : row,
    );
  if (!canViewCostDetails.value) {
    filas = filas.filter(
      (row) =>
        SECCIONES_VISIBLES_SIN_DETALLE_COSTOS.includes(row.SeccionGrilla) ||
        row.PrecioUnitario === true,
    );
  }
  const alternativas = props.cotizacion.CotizacionCantidad || [];
  const suffixes = alternativas.map((_, i) => i + 1);

  const gruposMap = new Map();
  filas.forEach((row, idx) => {
    const esAgrupable = SECCIONES_AGRUPABLES.includes(row.SeccionGrilla) && !!row.AgrupacionCosto;
    if (!esAgrupable) return;
    const key = `${row.SeccionGrilla}::${row.AgrupacionCosto}`;
    if (!gruposMap.has(key)) gruposMap.set(key, []);
    gruposMap.get(key).push({ row, originalIndex: idx });
  });

  const gruposReales = new Map();
  gruposMap.forEach((entries, key) => {
    if (entries.length >= 2) gruposReales.set(key, entries);
  });

  const resultado = [];
  const gruposYaInsertados = new Set();

  const impuestosChildren = [];
  let indexNodoImpuestos = -1;

  filas.forEach((row, idx) => {
    if (esFilaImpuesto(row)) {
      impuestosChildren.push({ ...row, __rowKey: `costo-${idx}` });
      if (indexNodoImpuestos === -1) {
        indexNodoImpuestos = resultado.length;
        resultado.push(null);
      }
      return;
    }

    const esAgrupable = SECCIONES_AGRUPABLES.includes(row.SeccionGrilla) && !!row.AgrupacionCosto;
    if (!esAgrupable) {
      resultado.push({ ...row, __rowKey: `costo-${idx}` });
      return;
    }

    const key = `${row.SeccionGrilla}::${row.AgrupacionCosto}`;
    const entries = gruposReales.get(key);

    if (!entries) {
      resultado.push({ ...row, __rowKey: `costo-${idx}` });
      return;
    }

    if (gruposYaInsertados.has(key)) return;
    gruposYaInsertados.add(key);

    const children = entries.map((entry) => ({
      ...entry.row,
      __rowKey: `costo-${entry.originalIndex}`,
    }));

    resultado.push(construirNodoGrupo(row.SeccionGrilla, row.AgrupacionCosto, children, suffixes));
  });

  if (indexNodoImpuestos !== -1) {
    resultado[indexNodoImpuestos] = construirNodoImpuestos(impuestosChildren, suffixes);
  }

  const ultimoIndexTotales = resultado.reduce(
    (acc, row, i) =>
      row.SeccionGrilla === 'Totales' || row.SeccionGrilla === 'Impuestos' ? i : acc,
    -1,
  );
  if (ultimoIndexTotales !== -1) {
    resultado.splice(ultimoIndexTotales + 1, 0, {
      Descripcion: conUnidadVenta('Precio de referencia'),
      SeccionGrilla: 'Totales',
      EsFilaPrecioReferencia: true,
      MedidaAbreviada: '',
      __rowKey: 'sintetica-precio-referencia',
    });
  }

  return resultado;
});

const CAMPOS_PORCENTAJE_CANTIDAD = [
  ['Utilidad', 'Utilidad'],
  ['Comision', 'Comision'],
  ['Flete', 'PorcentajeFlete'],
  ['MermaAdicional', 'MermaAdicional'],
  ['AlicuotaIVA', 'AlicuotaIVA'],
];

const valorCeldaParaCopiar = (row, col) => {
  const alt = props.cotizacion.CotizacionCantidad?.[col.altIndex] || {};
  if (col.field === 'Cantidad') {
    const campo = CAMPOS_PORCENTAJE_CANTIDAD.find(([rowField]) => row[rowField] !== undefined);
    if (campo) return alt[campo[1]] ?? '';
  }
  if (col.field === 'Precio' && row.EsFilaPrecioReferencia) {
    return formatColumnValue({ field: 'PrecioReferencia', suffix: '' }, alt);
  }
  const valor = formatColumnValue(col, row);
  return col.field === 'Cantidad' && valor === '' ? (row[`Cantidad${col.suffix}`] ?? '') : valor;
};

const sanitizarCeldaTsv = (valor) =>
  String(valor ?? '')
    .replace(/[\t\r\n]+/g, ' ')
    .trim();

const construirTsvGrilla = () => {
  const columnas = columnsConfig.value;
  const etiquetaGrupo = (col) => {
    const alt = props.cotizacion.CotizacionCantidad?.[col.altIndex];
    return `Cantidad ${col.suffix}: ${alt ? alt.Cantidad : ''} ${abreviaturaUnidadVenta.value}`.trim();
  };

  const encabezado = [
    'Costo',
    'Medida',
    ...columnas.map((col) => `${etiquetaGrupo(col)} - ${col.baseLabel}`),
  ];

  const lineas = [];
  const agregarFila = (row, nivel) => {
    const descripcion = `${'  '.repeat(nivel)}${obtenerDescripcionFormateada(row) ?? ''}`;
    lineas.push([
      descripcion,
      row.MedidaAbreviada ?? '',
      ...columnas.map((col) => valorCeldaParaCopiar(row, col)),
    ]);
    (row.children || []).forEach((hijo) => agregarFila(hijo, nivel + 1));
  };
  bloquesOrdenados.value.forEach((bloque, i) => {
    if (i > 0) lineas.push([]);
    if (bloque.titulo) lineas.push([bloque.titulo]);
    lineas.push(encabezado);
    bloque.datos.forEach((row) => agregarFila(row, 0));
  });

  return lineas.map((fila) => fila.map(sanitizarCeldaTsv).join('\t')).join('\r\n');
};

const copiarGrilla = async () => {
  try {
    await navigator.clipboard.writeText(construirTsvGrilla());
    useMessageSuccess().open('La grilla se copió al portapapeles.');
  } catch (err) {
    useMessageError().open('No fue posible copiar la grilla al portapapeles.');
  }
};

const FIELD_TO_BACKEND_KEY = {
  Cantidad: 'cantidad',
  PrecioUnitario: 'precioUnitario',
  Precio: 'precio',
  ValorUnidadVenta: 'valorUnidadVenta',
  ValorUnidadProducto: 'valorUnidadProducto',
};

const construirOrdenFilasPdf = () => {
  const { prop, order } = ordenActual.value;
  if (!prop || !order) return undefined;
  const direccion = order === 'descending' ? 'desc' : 'asc';
  if (prop === 'Descripcion') return { key: 'descripcion', direccion };
  if (prop === 'MedidaAbreviada') return { key: 'medida', direccion };
  const [, field, sufijo] = prop.match(/^(\D+)(\d+)$/) ?? [];
  const key = FIELD_TO_BACKEND_KEY[field];
  return key ? { key, opcion: Number(sufijo), direccion } : undefined;
};

const construirPayloadPdf = () => ({
  alternativas: (props.cotizacion.CotizacionCantidad || []).map((_, altIndex) => ({
    opcion: altIndex + 1,
    columnas: columnasEfectivas.value
      .filter((col) => col.altIndex === altIndex && FIELD_TO_BACKEND_KEY[col.field])
      .map((col, i) => ({
        key: FIELD_TO_BACKEND_KEY[col.field],
        visible: col.visible,
        orden: i + 1,
      })),
  })),
  formatoDescripcion: formatoDescripcion.value,
  ordenFilas: construirOrdenFilasPdf(),
});

const imprimirPdf = async () => {
  try {
    useLoading().openLoading('Generando PDF...');
    const data = await cotizacionCostosService.exportarGrillaPdf(
      props.cotizacion.Codigo,
      construirPayloadPdf(),
    );
    if (data?.success && data.url) {
      window.open(resolveApiUrl(data.url), '_blank');
      useMessageSuccess().open(data.mensaje || 'El PDF se generó correctamente.');
    } else {
      useMessageError().open(data?.mensaje || 'No fue posible generar el PDF.');
    }
  } catch (err) {
    useMessageError().open(mensajeErrorApi(err, 'No fue posible generar el PDF.'));
  } finally {
    useLoading().closeLoading();
  }
};

const ordenActual = ref({ prop: null, order: null });

const onSortChange = ({ prop, order }) => {
  ordenActual.value = order ? { prop, order } : { prop: null, order: null };
};

const defaultSort = computed(() =>
  ordenActual.value.prop
    ? { prop: ordenActual.value.prop, order: ordenActual.value.order }
    : undefined,
);

watch(gruposColumnas, (grupos) => {
  const prop = ordenActual.value.prop;
  if (!prop || prop === 'Descripcion' || prop === 'MedidaAbreviada') return;
  const existe = grupos.some((g) => g.columns.some((c) => `${c.field}${c.suffix}` === prop));
  if (!existe) ordenActual.value = { prop: null, order: null };
});

const esVacio = (v) => v === null || v === undefined || v === '' || Number.isNaN(Number(v));

const valorOrdenable = (row, prop) => {
  if (prop === 'Descripcion') return obtenerDescripcionFormateada(row) ?? '';
  if (prop === 'MedidaAbreviada') return row.MedidaAbreviada ?? '';
  return row[prop];
};

const ordenarFilas = (filas, { prop, order }) => {
  const dir = order === 'descending' ? -1 : 1;
  const esTexto = prop === 'Descripcion' || prop === 'MedidaAbreviada';
  return [...filas].sort((a, b) => {
    const va = valorOrdenable(a, prop);
    const vb = valorOrdenable(b, prop);
    if (esTexto) {
      return (
        dir * String(va).localeCompare(String(vb), 'es', { numeric: true, sensitivity: 'base' })
      );
    }
    const vacioA = esVacio(va);
    const vacioB = esVacio(vb);
    if (vacioA || vacioB) return vacioA === vacioB ? 0 : vacioA ? 1 : -1;
    return dir * (Number(va) - Number(vb));
  });
};

// Solo se ordenan las filas de detalle (antes de "Costo total"); el resto queda fijo.
// Los bloques sin fila "Costo total" (Adicionales) se ordenan completos.
const ordenarBloque = (datos) => {
  if (!ordenActual.value.order) return datos;
  const idxCorte = datos.findIndex((row) => row.CostoTotal === true);
  const limite = idxCorte === -1 ? datos.length : idxCorte;

  const cabecera = ordenarFilas(datos.slice(0, limite), ordenActual.value).map((row) =>
    row.children ? { ...row, children: ordenarFilas(row.children, ordenActual.value) } : row,
  );
  return [...cabecera, ...datos.slice(limite)];
};

const bloquesGrilla = computed(() => {
  const adicionales = datosGrillaAgrupados.value.filter((r) => r.SeccionGrilla === 'Adicionales');
  const principal = datosGrillaAgrupados.value.filter((r) => r.SeccionGrilla !== 'Adicionales');
  const bloques = [{ clave: 'principal', titulo: null, datos: principal }];
  if (adicionales.length) {
    const suffixes = (props.cotizacion.CotizacionCantidad || []).map((_, i) => i + 1);
    bloques.push({
      clave: 'adicionales',
      titulo: 'Adicionales',
      datos: [...adicionales, construirFilaTotalAdicionales(adicionales, suffixes)],
    });
  }
  return bloques;
});

const bloquesOrdenados = computed(() =>
  bloquesGrilla.value.map((bloque) => ({ ...bloque, datos: ordenarBloque(bloque.datos) })),
);

const obtenerClaseFila = ({ row }) => {
  const clases = [];
  if (SECCION_CLASS_MAP[row.SeccionGrilla]) clases.push(SECCION_CLASS_MAP[row.SeccionGrilla]);
  if (row.EsGrupoSubtotal) clases.push('grupo-subtotal-row');
  if (row.SeccionGrilla === 'Impuestos') clases.push('grupo-impuestos-row');
  if (row.EsFilaPrecioReferencia) clases.push('fila-precio-referencia');
  if (row.PrecioUnitario === true) clases.push('fila-precio-unitario');
  if (row.CostoTotal === true) clases.push('fila-costo-total');
  return clases.join(' ');
};

let isApplyingConfig = false;

const cargarConfiguracion = () => {
  const defaults = buildDefaultColumnsConfig();
  let saved = null;
  try {
    saved = JSON.parse(localStorage.getItem(storageKey.value));
  } catch {
    saved = null;
  }

  if (!Array.isArray(saved) || !saved.length) {
    isApplyingConfig = true;
    columnsConfig.value = defaults;
    isApplyingConfig = false;
    return;
  }

  const byId = new Map(defaults.map((c) => [c.id, c]));
  const ordered = [];
  saved.forEach((s) => {
    const col = byId.get(s.id);
    if (col) {
      ordered.push({ ...col, visible: s.visible });
      byId.delete(s.id);
    }
  });
  byId.forEach((col) => ordered.push(col));

  isApplyingConfig = true;
  columnsConfig.value = ordered;
  isApplyingConfig = false;
};

const guardarConfiguracion = () => {
  try {
    const payload = columnsConfig.value.map(({ id, visible }) => ({ id, visible }));
    localStorage.setItem(storageKey.value, JSON.stringify(payload));
  } catch {}
};

const cargarConfiguracionGlobal = () => {
  const defaults = buildDefaultGlobalConfig();
  let saved = null;
  try {
    saved = JSON.parse(localStorage.getItem(globalStorageKey.value));
  } catch {
    saved = null;
  }

  isApplyingConfig = true;
  if (!Array.isArray(saved) || !saved.length) {
    globalConfig.value = defaults;
  } else {
    const byField = new Map(defaults.map((c) => [c.field, c]));
    const ordered = [];
    saved.forEach((s) => {
      const item = byField.get(s?.field);
      if (item) {
        ordered.push({ field: item.field, visible: !!s.visible });
        byField.delete(s.field);
      }
    });
    byField.forEach((item) => ordered.push(item));
    globalConfig.value = ordered;
  }
  isApplyingConfig = false;
};

const guardarConfiguracionGlobal = () => {
  try {
    localStorage.setItem(globalStorageKey.value, JSON.stringify(globalConfig.value));
  } catch {}
};

const cargarModoVista = () => {
  try {
    const saved = localStorage.getItem(modoVistaStorageKey.value);
    if (MODOS_VISTA_VALIDOS.includes(saved)) modoVista.value = saved;
  } catch {}
};

const restablecerPorDefecto = () => {
  if (esModoGlobal.value) {
    try {
      localStorage.removeItem(globalStorageKey.value);
    } catch {}
    isApplyingConfig = true;
    globalConfig.value = buildDefaultGlobalConfig();
    isApplyingConfig = false;
    guardarConfiguracionGlobal();
    return;
  }
  try {
    localStorage.removeItem(storageKey.value);
  } catch {}
  isApplyingConfig = true;
  columnsConfig.value = buildDefaultColumnsConfig();
  isApplyingConfig = false;
  guardarConfiguracion();
};

const inicializarDragAndDrop = () => {
  if (sortableInstance) {
    sortableInstance.destroy();
    sortableInstance = null;
  }
  const global = esModoGlobal.value;
  const el = global ? globalListRef.value : columnListRef.value;
  if (!el) return;
  const target = global ? globalConfig : columnsConfig;
  sortableInstance = Sortable.create(el, {
    animation: 150,
    handle: '.drag-handle',
    onEnd: (evt) => {
      const { oldIndex, newIndex } = evt;
      if (oldIndex === newIndex) return;
      const newArray = [...target.value];
      const moved = newArray.splice(oldIndex, 1)[0];
      newArray.splice(newIndex, 0, moved);
      target.value = newArray;
    },
  });
};

const onPopoverShow = () => {
  nextTick(() => inicializarDragAndDrop());
};

const onPopoverHide = () => {
  if (sortableInstance) {
    sortableInstance.destroy();
    sortableInstance = null;
  }
};

watch(
  () => props.cotizacion.CotizacionCantidad?.length,
  () => cargarConfiguracion(),
);

watch(
  () => (props.cotizacion.CotizacionCantidad || []).map((a) => [a.Codigo, a.PrecioReferencia]),
  sincronizarPrecioReferencia,
  { immediate: true, deep: true },
);

watch(
  columnsConfig,
  () => {
    if (isApplyingConfig) return;
    guardarConfiguracion();
  },
  { deep: true },
);

watch(
  globalConfig,
  () => {
    if (isApplyingConfig) return;
    guardarConfiguracionGlobal();
  },
  { deep: true },
);

watch(modoVista, (value) => {
  try {
    localStorage.setItem(modoVistaStorageKey.value, value);
  } catch {}
  nextTick(() => {
    if (sortableInstance) inicializarDragAndDrop();
  });
});

onMounted(() => {
  cargarConfiguracion();
  cargarConfiguracionGlobal();
  cargarModoVista();
  cargarFormatoDescripcion();
});
</script>

<style scoped>
.nueva-grilla-costos {
  padding: 20px 0;
}

.grilla-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 12px;
}

.grilla-header__info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.grilla-header__title {
  font-size: 18px;
  font-weight: 600;
  line-height: 1.3;
  color: var(--el-text-color-primary);
}

.grilla-header__subtitle {
  font-size: 13px;
  color: var(--el-text-color-secondary);
}

.grilla-header__actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.grilla-header__actions :deep(.el-button + .el-button) {
  margin-left: 0;
}

.grilla-cantidades {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 8px 12px;
  margin-bottom: 12px;
  background-color: var(--el-fill-color-lighter);
  border: 1px solid var(--el-border-color-lighter);
  border-radius: 6px;
}

.grilla-cantidades__label {
  font-size: 13px;
  font-weight: 500;
  color: var(--el-text-color-secondary);
}

.navegador-cantidad {
  display: flex;
  align-items: center;
  gap: 4px;
}

.vista-config {
  display: flex;
  flex-direction: column;
}

.vista-config__section {
  padding: 10px 0;
  border-bottom: 1px solid var(--el-border-color-lighter);
}

.vista-config__section:first-child {
  padding-top: 0;
}

.vista-config__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.vista-config__title {
  font-size: 13px;
  font-weight: 600;
  color: var(--el-text-color-primary);
}

.vista-config__hint {
  font-size: 12px;
  color: var(--el-text-color-secondary);
}

.vista-config__radios {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-top: 4px;
}

.vista-config__footer {
  display: flex;
  justify-content: flex-end;
  padding-top: 8px;
}

.column-config-list {
  max-height: 240px;
  overflow-y: auto;
  margin-top: 4px;
}

.column-config-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
}

.drag-handle {
  cursor: grab;
  color: var(--el-text-color-secondary);
  flex-shrink: 0;
}

.nueva-grilla-costos :deep(.section-totales),
.nueva-grilla-costos :deep(.section-totales:hover > td.el-table__cell) {
  background-color: #e3eefc;
}

.nueva-grilla-costos :deep(.section-totales) td {
  font-weight: bold;
}

.nueva-grilla-costos :deep(.section-totales.fila-precio-referencia),
.nueva-grilla-costos :deep(.section-totales.fila-precio-referencia:hover > td.el-table__cell) {
  background-color: var(--el-fill-color-light);
}

.grilla-bloque + .grilla-bloque {
  margin-top: 24px;
}

.grilla-bloque__titulo {
  margin: 0 0 8px;
  font-size: 16px;
  font-weight: 600;
  color: var(--el-text-color-primary);
}

.nueva-grilla-costos :deep(.section-ajustes) {
  background-color: var(--el-fill-color-lighter);
}

.nueva-grilla-costos :deep(.section-utilidad) {
  background-color: var(--el-color-warning-light-9);
}

.nueva-grilla-costos :deep(.fila-costo-total),
.nueva-grilla-costos :deep(.fila-costo-total:hover > td.el-table__cell) {
  background-color: var(--el-color-success-light-9);
}

.nueva-grilla-costos :deep(.fila-costo-total) td.el-table__cell {
  border-top: 2px solid var(--el-border-color-darker);
  font-weight: bold;
}

.nueva-grilla-costos :deep(.grupo-subtotal-row) {
  background-color: var(--el-fill-color-lighter);
}

.nueva-grilla-costos :deep(.grupo-subtotal-row) td {
  font-weight: bold;
}

.nueva-grilla-costos :deep(.grupo-subtotal-row.grupo-impuestos-row) td {
  font-weight: normal;
}

.nueva-grilla-costos :deep(.grupo-alternativa-column) {
  border-right: 2px solid #c0c0c09e !important;
}

.nueva-grilla-costos :deep(.el-table th.el-table__cell.fin-grupo-alternativa),
.nueva-grilla-costos :deep(.el-table td.el-table__cell.fin-grupo-alternativa) {
  border-right: 3px solid #a8abb2 !important;
}

.nueva-grilla-costos :deep(.el-table tr.fila-precio-unitario td.el-table__cell),
.nueva-grilla-costos :deep(.el-table tr.section-totales td.el-table__cell) {
  padding: 14px 0;
}

.nueva-grilla-costos.modo-compacto :deep(.el-table .cell) {
  padding-left: 6px;
  padding-right: 6px;
}

.nueva-grilla-costos.modo-compacto :deep(.el-table th.el-table__cell),
.nueva-grilla-costos.modo-compacto :deep(.el-table td.el-table__cell) {
  padding: 4px 0;
}

.celda-descripcion {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.descripcion-personalizada {
  font-style: italic;
}

.btn-eliminar-concepto {
  display: none;
}

.btn-cambiar-adicional {
  visibility: hidden;
}

.celda-descripcion:hover .btn-cambiar-adicional,
.celda-descripcion:focus-within .btn-cambiar-adicional,
.btn-cambiar-adicional.is-loading {
  visibility: visible;
}

.celda-descripcion:hover .btn-eliminar-concepto,
.btn-eliminar-concepto.is-loading {
  display: inline-flex;
}

.precio-referencia-celda {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.input-precio-referencia {
  width: 100%;
}

.precio-referencia-acciones {
  position: absolute;
  left: 0;
  z-index: 1;
  display: none;
  align-items: center;
  gap: 4px;
  padding-right: 4px;
  background-color: var(--el-fill-color-light);
}

.precio-referencia-celda:hover .precio-referencia-acciones,
.precio-referencia-celda:focus-within .precio-referencia-acciones,
.precio-referencia-acciones.acciones--visibles {
  display: inline-flex;
}

.precio-referencia-acciones :deep(.el-button + .el-button) {
  margin-left: 0;
}

.precio-referencia-acciones :deep(.el-button.is-circle) {
  width: 22px;
  height: 22px;
  padding: 0;
}

.celda-editable {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-height: 22px;
}

.icon-edit {
  cursor: pointer;
  flex-shrink: 0;
}

.cell-actions {
  display: none;
  align-items: center;
  gap: 4px;
}

.celda-editable:hover .cell-actions {
  display: flex;
}

.badge-editado {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--el-color-warning);
  flex-shrink: 0;
}
</style>
